import React, { useState, useEffect, useRef, useMemo } from "react";
import { 
    Card, Grid, Icon, IconButton, InputLabel, Tooltip, Box, 
    Avatar, MenuItem, Select, FormControl, Divider, Badge, Checkbox, ListItemText
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDSelect from "components/MDSelect";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useMaterialUIController } from "context";
import { useUser } from "context/UserContext";
import { get } from "utils/WebService";
import routes from "routes";

export default function MonitoriaSystem({ standalone = false }) {
    const { userFunctionalities, userPerfils, setUserPerfils } = useUser();
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const [equipes, setEquipes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEquipes, setSelectedEquipes] = useState([]);
    const [refreshInterval, setRefreshInterval] = useState(30); // seconds
    const [currentTime, setCurrentTime] = useState(new Date());

    // Ref to hold current state to prevent stale closure in setInterval
    const refreshTimer = useRef(null);

    // Standalone profile loader
    useEffect(() => {
        if (!userPerfils || userPerfils.length === 0) {
            get('/users/by-token')
                .then((userData) => {
                    if (userData && userData.id) {
                        get(`/users/by-id/${userData.id}`)
                            .then((fullUser) => {
                                if (fullUser && fullUser.perfils) {
                                    setUserPerfils(fullUser.perfils);
                                }
                            })
                            .catch(err => console.error("Erro ao carregar perfil no standalone", err));
                    }
                })
                .catch(err => console.error("Erro ao carregar token no standalone", err));
        }
    }, [userPerfils, setUserPerfils]);

    const isLiberado = userPerfils?.some(p => {
        const n = (p.nome || p.ccNome || '').toLowerCase();
        return n.includes("gestor") || n.includes("admin") || n.includes("coord");
    });

    const isAdminOrGestor = useMemo(() => {
        return userPerfils?.some(p => {
            const n = (p.nome || p.ccNome || '').toLowerCase();
            return n.includes("gestor") || n.includes("admin");
        });
    }, [userPerfils]);

    const fetchMonitoriaData = () => {
        setLoading(true);
        get("/users/coordenador/equipes")
            .then((res) => {
                if (Array.isArray(res)) {
                    setEquipes(res);
                    // Select first team as default if nothing has been selected yet
                    setSelectedEquipes(prev => {
                        if (prev.length === 0 && res.length > 0) {
                            return [res[0].id];
                        }
                        return prev;
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error("Error fetching monitoria data:", err);
                setLoading(false);
            });
    };

    // Auto-refresh timer
    useEffect(() => {
        fetchMonitoriaData();
        
        // Update current time every second for dynamic durations
        const secTimer = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        return () => {
            clearInterval(secTimer);
        };
    }, []);

    useEffect(() => {
        if (refreshTimer.current) {
            clearInterval(refreshTimer.current);
        }

        if (refreshInterval > 0) {
            refreshTimer.current = setInterval(() => {
                fetchMonitoriaData();
            }, refreshInterval * 1000);
        }

        return () => {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
            }
        };
    }, [refreshInterval]);

    // Format helper functions
    const formatDuration = (dateStart) => {
        if (!dateStart) return "-";
        const diffMs = currentTime.getTime() - new Date(dateStart).getTime();
        if (diffMs < 0) return "0s";
        const secs = Math.floor(diffMs / 1000);
        const mins = Math.floor(secs / 60);
        const hrs = Math.floor(mins / 60);
        if (hrs > 0) return `${hrs}h ${mins % 60}m`;
        if (mins > 0) return `${mins}m ${secs % 60}s`;
        return `${secs}s`;
    };

    const formatTimeAgo = (dateLast) => {
        if (!dateLast) return "-";
        const diffMs = currentTime.getTime() - new Date(dateLast).getTime();
        if (diffMs < 0) return "agora";
        const secs = Math.floor(diffMs / 1000);
        if (secs < 60) return `${secs}s atrás`;
        const mins = Math.floor(secs / 60);
        if (mins < 60) return `${mins}m atrás`;
        return `${Math.floor(mins / 60)}h atrás`;
    };

    const formatSeconds = (totalSeconds) => {
        if (!totalSeconds) return "0s";
        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;
        if (hrs > 0) return `${hrs}h ${mins % 60}m`;
        if (mins > 0) return `${mins}m ${secs}s`;
        return `${secs}s`;
    };

    // Calculate general stats
    const stats = useMemo(() => {
        let totalMembros = 0;
        let onlineMembros = 0;
        let emAtendimento = 0;

        equipes.forEach(eq => {
            const isSelected = selectedEquipes.includes("all") || selectedEquipes.includes(eq.id);
            if (isSelected) {
                eq.membros?.forEach(m => {
                    totalMembros++;
                    if (m.online) {
                        onlineMembros++;
                    }
                    if (m.idDevedor) {
                        emAtendimento++;
                    }
                });
            }
        });

        return { totalMembros, onlineMembros, emAtendimento };
    }, [equipes, selectedEquipes]);

    // Handle opening of devedor record
    const handleOpenDevedor = (idDevedor) => {
        const baseDeDadosRoute = routes
            .flatMap(r => r.collapse ? r.collapse : r)
            .find(r => r.key === "baseDeDados");

        if (standalone) {
            // We are in standalone tab: copy session token briefly to localStorage and open main system
            const token = sessionStorage.getItem('user');
            const userMenus = sessionStorage.getItem('userMenus');
            
            localStorage.setItem('temp_share_user', token);
            localStorage.setItem('temp_share_menus', userMenus);
            localStorage.setItem('temp_share_selected_pages', JSON.stringify(["home", "baseDeDados"]));
            
            // Set devedor to be preloaded
            sessionStorage.setItem('pendingDevedorId', idDevedor);
            window.open('/system', '_blank');
        } else {
            // We are inside the main system tab: load devedor and switch tab
            sessionStorage.setItem('pendingDevedorId', idDevedor);
            // Dispatch page select to SystemWrapper (passed via props/clone)
            if (window.onPageSelectCustom) {
                window.onPageSelectCustom(baseDeDadosRoute);
            }
        }
    };

    const handlePopout = () => {
        const token = sessionStorage.getItem('user');
        const userMenus = sessionStorage.getItem('userMenus');
        
        localStorage.setItem('temp_share_user', token);
        localStorage.setItem('temp_share_menus', userMenus);
        window.open('/system?view=monitoria', '_blank');
    };

    const getInitials = (name) => {
        if (!name) return "";
        const parts = name.split(" ");
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return name.slice(0, 2).toUpperCase();
    };

    const getRandomColor = (name) => {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const colors = [
            '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', 
            '#2196f3', '#009688', '#4caf50', '#ff9800', 
            '#ff5722', '#795548'
        ];
        return colors[Math.abs(hash) % colors.length];
    };

    // Prepare table data
    const tableColumns = [
        { Header: "Operador", accessor: "nome", width: "15%" },
        { Header: "Status", accessor: "status", width: "10%", align: "center" },
        { Header: "Ação Corrente", accessor: "ultimaAcao", width: "15%" },
        { Header: "Login / Logout", accessor: "loginLogout", width: "10%" },
        { Header: "T. Logado", accessor: "tempoLogado", width: "10%" },
        { Header: "T. Pausa", accessor: "tempoPausa", width: "8%", align: "center" },
        { Header: "Fichas (Fila / Trab)", accessor: "fichas", width: "10%", align: "center" },
        { Header: "Acordos", accessor: "acordos", width: "10%", align: "center" },
        { Header: "Ficha Aberta", accessor: "fichaAberta", width: "5%", align: "center" }
    ];

    const tableRows = useMemo(() => {
        const rows = [];
        equipes.forEach(eq => {
            const isSelected = selectedEquipes.includes("all") || selectedEquipes.includes(eq.id);
            if (isSelected && eq.membros) {
                eq.membros.forEach(m => {
                    
                    let statusNode;
                    if (m.emPausa) {
                        statusNode = (
                            <MDBox display="flex" alignItems="center">
                                <Icon color="warning" sx={{ mr: 0.5, fontSize: "16px !important" }}>pause_circle</Icon>
                                <MDTypography variant="caption" color="warning" fontWeight="bold">Em Pausa</MDTypography>
                            </MDBox>
                        );
                    } else if (m.online) {
                        statusNode = (
                            <MDBox display="flex" alignItems="center">
                                <span className="pulse-active" style={{ height: '10px', width: '10px', borderRadius: '50%', backgroundColor: '#4caf50', display: 'inline-block', marginRight: '6px' }} />
                                <MDTypography variant="caption" color="success" fontWeight="bold">Online</MDTypography>
                            </MDBox>
                        );
                    } else {
                        statusNode = (
                            <MDBox display="flex" alignItems="center">
                                <span style={{ height: '10px', width: '10px', borderRadius: '50%', backgroundColor: '#9e9e9e', display: 'inline-block', marginRight: '6px' }} />
                                <MDTypography variant="caption" color="text" fontWeight="medium">Offline</MDTypography>
                            </MDBox>
                        );
                    }

                    // Calculate current pause time
                    let pauseTimeStr = "-";
                    if (m.emPausa && m.dataInicioPausa) {
                        const start = new Date(m.dataInicioPausa).getTime();
                        const elapsed = Math.floor((currentTime.getTime() - start) / 1000);
                        pauseTimeStr = formatSeconds((m.tempoTotalPausa || 0) + elapsed);
                    } else if (m.tempoTotalPausa > 0) {
                        pauseTimeStr = formatSeconds(m.tempoTotalPausa);
                    }

                    rows.push({
                        nome: (
                            <MDBox display="flex" alignItems="center">
                                <Avatar sx={{ bgcolor: getRandomColor(m.nome), width: 30, height: 30, fontSize: "0.8rem", fontWeight: "bold", mr: 1 }}>
                                    {getInitials(m.nome)}
                                </Avatar>
                                <MDBox display="flex" flexDirection="column">
                                    <MDTypography variant="caption" fontWeight="bold">{m.nome}</MDTypography>
                                    <MDTypography variant="caption" color="text" sx={{ fontSize: "10px" }}>{m.email}</MDTypography>
                                </MDBox>
                            </MDBox>
                        ),
                        status: statusNode,
                        ultimaAcao: (
                            <MDTypography variant="caption" color={m.emPausa ? "warning" : "info"} fontWeight="bold" sx={{ maxWidth: "120px", display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {m.online || m.emPausa ? (m.ultimaAcao || "Sem atividade") : "Offline"}
                            </MDTypography>
                        ),
                        loginLogout: (
                            <MDBox display="flex" flexDirection="column">
                                <MDTypography variant="caption" color="text">In: {m.dataLogin ? new Date(m.dataLogin).toLocaleTimeString() : "-"}</MDTypography>
                                <MDTypography variant="caption" color="text">Out: {m.dataLogout ? new Date(m.dataLogout).toLocaleTimeString() : "-"}</MDTypography>
                            </MDBox>
                        ),
                        tempoLogado: (
                            <MDTypography variant="caption" fontWeight="medium">
                                {m.dataLogin ? formatDuration(m.dataLogin) : "-"}
                            </MDTypography>
                        ),
                        tempoPausa: (
                            <MDTypography variant="caption" color={m.emPausa ? "warning" : "text"} fontWeight="bold">
                                {pauseTimeStr}
                            </MDTypography>
                        ),
                        fichas: (
                            <MDTypography variant="caption" fontWeight="medium">
                                <span style={{color: '#4caf50'}}>{m.fichasFinalizadas || 0}</span> / <span style={{color: '#2196f3'}}>{m.fichasTrabalhadas || 0}</span>
                            </MDTypography>
                        ),
                        acordos: (
                            <Tooltip title={`À Vista: ${m.acordosAVista || 0} | Parcelados: ${m.acordosParcelados || 0}`}>
                                <MDTypography variant="caption" fontWeight="bold" color="success">
                                    Total: {m.totalAcordos || 0}
                                </MDTypography>
                            </Tooltip>
                        ),
                        fichaAberta: m.idDevedor ? (
                            <Tooltip title={`Ficha: ${m.nomeDevedor || "Aberto"} (${formatDuration(m.dataAberturaFicha)})`}>
                                <IconButton size="small" color="info" onClick={() => handleOpenDevedor(m.idDevedor)}>
                                    <Icon fontSize="small">visibility</Icon>
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <MDTypography variant="caption" color="text">-</MDTypography>
                        )
                    });
                });
            }
        });
        return rows;
    }, [equipes, selectedEquipes, currentTime]);

    return (
        <MDBox py={standalone ? 2 : 1}>
            <style>{`
                @keyframes pulse {
                    0% {
                        transform: scale(0.9);
                        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
                    }
                    70% {
                        transform: scale(1);
                        box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
                    }
                    100% {
                        transform: scale(0.9);
                        box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
                    }
                }
                .pulse-active {
                    animation: pulse 2s infinite;
                }
            `}</style>

            {/* Header controls */}
            <Card variant="outlined" sx={{ p: 2, mb: 2, background: darkMode ? "#23233a" : "#fafafa", borderColor: darkMode ? "#333" : "#ddd" }}>
                <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                    <Grid item xs={12} md={4} display="flex" alignItems="center">
                        <Icon color="info" fontSize="large" sx={{ mr: 1.5 }}>insights</Icon>
                        <MDBox ml={2} lineHeight={1}>
                            <MDTypography variant="h5" fontWeight="medium">
                                Painel de Monitoria
                            </MDTypography>
                        </MDBox>
                    </Grid>

                    <Grid item xs={12} md={8} display="flex" flexWrap="wrap" justifyContent={{ xs: "flex-start", md: "flex-end" }} gap={2}>
                        <MDBox width="250px">
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Filtrar Equipe</InputLabel>
                            <MDSelect
                                isMulti={isAdminOrGestor}
                                getOptionValue={option => option.id}
                                getOptionLabel={option => option.nome}
                                name="filtro-equipes"
                                placeholder="Todas as Equipes"
                                value={
                                    isAdminOrGestor 
                                    ? (selectedEquipes.includes("all") || selectedEquipes.length === 0 ? [{id: "all", nome: "Todas as Equipes"}] : equipes.filter(e => selectedEquipes.includes(e.id))) 
                                    : (selectedEquipes.length > 0 && selectedEquipes[0] !== "all" ? equipes.find(e => e.id === selectedEquipes[0]) : {id: "all", nome: "Todas as Equipes"})
                                }
                                options={equipes.length > 0 ? [{id: "all", nome: "Todas as Equipes"}, ...equipes] : [{id: "all", nome: "Todas as Equipes"}]}
                                onChange={selectedOptions => {
                                    if (!selectedOptions) {
                                        setSelectedEquipes(["all"]);
                                        return;
                                    }
                                    if (isAdminOrGestor) {
                                        if (Array.isArray(selectedOptions)) {
                                            if (selectedOptions.length === 0) {
                                                setSelectedEquipes(["all"]);
                                            } else if (selectedOptions[selectedOptions.length - 1].id === "all") {
                                                setSelectedEquipes(["all"]);
                                            } else {
                                                setSelectedEquipes(selectedOptions.filter(o => o.id !== "all").map(o => o.id));
                                            }
                                        }
                                    } else {
                                        setSelectedEquipes([selectedOptions.id]);
                                    }
                                }}
                            />
                        </MDBox>

                        {/* Refresh Rate */}
                        <MDBox width="150px">
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Atualizar a cada</InputLabel>
                            <Select
                                value={refreshInterval}
                                onChange={(e) => setRefreshInterval(e.target.value)}
                                displayEmpty
                                fullWidth
                                variant="outlined"
                                size="small"
                                sx={{ 
                                    height: "38px", 
                                    background: darkMode ? "#1a1a2e" : "#fff",
                                    color: darkMode ? "#fff" : "#000"
                                }}
                            >
                                <MenuItem value={0}>Manual</MenuItem>
                                <MenuItem value={15}>15s</MenuItem>
                                <MenuItem value={30}>30s</MenuItem>
                                <MenuItem value={60}>1 min</MenuItem>
                                <MenuItem value={120}>2 min</MenuItem>
                            </Select>
                        </MDBox>

                        <MDBox display="flex" alignItems="flex-end" pb={0.2} gap={1}>
                            <MDButton 
                                variant="gradient" 
                                color="info" 
                                size="medium"
                                onClick={fetchMonitoriaData}
                                disabled={loading}
                            >
                                <Icon>{loading ? "sync" : "refresh"}</Icon>
                                &nbsp;Atualizar
                            </MDButton>

                            {!standalone && (
                                <Tooltip title="Destacar em nova aba (Segundo Monitor)">
                                    <MDButton 
                                        variant="gradient" 
                                        color="dark" 
                                        size="medium"
                                        onClick={handlePopout}
                                    >
                                        <Icon>open_in_new</Icon>
                                        &nbsp;Destacar
                                    </MDButton>
                                </Tooltip>
                            )}
                        </MDBox>
                    </Grid>
                </Grid>
            </Card>

            {/* Stats row */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, background: darkMode ? "#1e1e30" : "#ffffff", borderColor: darkMode ? "#333" : "#eee" }}>
                        <MDBox display="flex" alignItems="center">
                            <MDBox p={1.5} borderRadius="lg" bg="info" color="white" display="flex" mr={2}>
                                <Icon fontSize="medium">groups</Icon>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="button" color="text" fontWeight="medium">Total de Operadores</MDTypography>
                                <MDTypography variant="h4" fontWeight="bold">{stats.totalMembros}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, background: darkMode ? "#1e1e30" : "#ffffff", borderColor: darkMode ? "#333" : "#eee" }}>
                        <MDBox display="flex" alignItems="center">
                            <MDBox p={1.5} borderRadius="lg" bg="success" color="white" display="flex" mr={2}>
                                <Icon fontSize="medium">online_prediction</Icon>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="button" color="text" fontWeight="medium">Ativos Agora</MDTypography>
                                <MDTypography variant="h4" fontWeight="bold" color="success">{stats.onlineMembros}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Card variant="outlined" sx={{ p: 2, background: darkMode ? "#1e1e30" : "#ffffff", borderColor: darkMode ? "#333" : "#eee" }}>
                        <MDBox display="flex" alignItems="center">
                            <MDBox p={1.5} borderRadius="lg" bg="warning" color="white" display="flex" mr={2}>
                                <Icon fontSize="medium">contact_phone</Icon>
                            </MDBox>
                            <MDBox>
                                <MDTypography variant="button" color="text" fontWeight="medium">Fichas em Atendimento</MDTypography>
                                <MDTypography variant="h4" fontWeight="bold" color="warning">{stats.emAtendimento}</MDTypography>
                            </MDBox>
                        </MDBox>
                    </Card>
                </Grid>
            </Grid>

            {/* Table layout instead of cards */}
            <Card variant="outlined" sx={{ background: darkMode ? "#1e1e30" : "#ffffff", borderColor: darkMode ? "#333" : "#eee" }}>
                <MDBox p={3} display="flex" alignItems="center">
                    <Icon color="info" sx={{ mr: 1 }}>view_list</Icon>
                    <MDTypography variant="h6" fontWeight="bold">
                        Lista de Operadores
                    </MDTypography>
                </MDBox>
                <Divider sx={{ m: 0 }} />
                <TableContainer>
                    <Table size="small">
                        <MDBox component="thead">
                            <TableRow>
                                {tableColumns.map((col, idx) => (
                                    <TableCell 
                                        key={idx} 
                                        align={col.align || "left"} 
                                        sx={{ width: col.width, color: darkMode ? "#fff" : "#333", fontWeight: "bold", fontSize: "12px", borderBottom: `1px solid ${darkMode ? "#333" : "#eee"}` }}
                                    >
                                        {col.Header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </MDBox>
                        <TableBody>
                            {tableRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={tableColumns.length} align="center" sx={{ py: 3, borderBottom: "none" }}>
                                        <MDTypography variant="button" color="text" fontStyle="italic">
                                            Nenhum operador encontrado para os filtros selecionados.
                                        </MDTypography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                tableRows.map((row, idx) => (
                                    <TableRow key={idx} sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                                        {tableColumns.map((col, colIdx) => (
                                            <TableCell key={colIdx} align={col.align || "left"} sx={{ borderBottom: `1px solid ${darkMode ? "#333" : "#eee"}` }}>
                                                {row[col.accessor]}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Card>
        </MDBox>
    );
}
